<template>
  <div class="card-reply">
    <div class="mb-2">
      <text-editor
        ref="textEditor"
        v-show="editMode"
        class="textEditor"
        :text="reply.text"
        v-model="updatedText"
        @text-update="onTextUpdate"
        @move-focus="(_, target) => target.firstChild.blur()"
        @on-enter="submitReply(updatedText)"
      />
      <p class="mb-0 text-black text" v-html="sanitizedReplyText" v-show="!editMode"/>
    </div>
    <div class="d-flex flex-nowrap align-center">
      <v-tooltip location="top" open-delay="500" v-if="withDates && reply.dateCreated && !editMode">
        <template v-slot:activator="{ on, props }">
          <span
            v-bind="props"
            class="text-nowrap reply-time"
            v-on="{ ...on }"
          >
            {{ formatDistanceToNowStrict(new Date(reply.dateCreated), {addSuffix: true}) }}
          </span>
        </template>

        <div>
          <p class="text-body-2 mb-0">
            Created: {{ format(new Date(reply.dateCreated), 'MMMM d, yyyy, HH:mm') }}
          </p>

          <p v-if="reply.dateUpdated" class="text-body-2 mb-0">
            Updated: {{ format(new Date(reply.dateUpdated), 'MMMM d, yyyy, HH:mm') }}
          </p>
        </div>
      </v-tooltip>

      <v-spacer/>
      <div class="d-flex align-center actions">
        <v-tooltip
          v-if="!reply.isDummy && !editMode"
          location="top"
          open-delay="300"
          :disabled="isAuthor"
        >
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="action-wrapper">
              <v-btn
                size="small"
                variant="plain"
                @click="handleEditReply"
                icon="mds:edit"
                :disabled="!isAuthor || $store.state.isWorkflowDisabled"
              />
            </span>
          </template>
          <span>You can only edit your own comments</span>
        </v-tooltip>

        <v-btn
          size="small"
          color="primary"
          variant="outlined"
          :loading="loading"
          class="card-reply-button"
          @mousedown="toggleEditForm"
          v-if="reply.author?.id === currentUser?.id && editMode && !reply.isDummy"
        >
          Cancel
        </v-btn>

        <v-tooltip v-if="editMode && reply.isDummy" open-delay="500" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              size="small"
              :color="isDarkTheme() ? 'white' : 'primary'"
              variant="outlined"
              :loading="loading"
              :disabled="isDisabled"
              class="card-reply-button"
              @click="submitToAi(updatedText)"
              v-bind="props"
            >
              <img
                class="logo"
                alt="Ask AI"
                src="@/assets/mage_stars.svg"
              />
              Ask AI
            </v-btn>
          </template>
          <span>Ask AI a question! NOTE: By clicking this button you will <br/>share information about your current workflow with Open AI.</span>
        </v-tooltip>
        <v-btn
          v-if="editMode"
          size="small"
          variant="flat"
          color="primary"
          :loading="loading"
          :disabled="isDisabled"
          class="card-reply-button"
          @click="submitReply(updatedText)"
        >
          Comment
        </v-btn>

        <v-tooltip
          v-if="!editMode && !reply.isDummy"
          location="top"
          open-delay="300"
          :disabled="canDelete"
        >
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="action-wrapper">
              <v-btn
                size="small"
                variant="plain"
                icon="mds:delete"
                @click="showDeleteDialog = true"
                :disabled="!canDelete || $store.state.isWorkflowDisabled"
              />
            </span>
          </template>
          <span>Only the author or the project owner can delete this comment</span>
        </v-tooltip>
      </div>

      <div class="avatar" v-if="!editMode">
        <card-user-avatar
          :large="false"
          :grayscale="true"
          :generated-by-ai="reply.generatedByAi"
          :fullname="authorName"
          :picture="reply.author?.picture"
        ></card-user-avatar>
      </div>
    </div>
    <delete-confirmation-dialog
      v-model="showDeleteDialog"
      title="Confirm comment deletion"
      text="Are you sure you want to delete the comment?"
      :onDelete="deleteCardComment"
    />
  </div>
</template>

<script>
import CardUserAvatar from '@/components/CardUserAvatar'
// import BpmnCardTextareaForm from '@/components/BpmnCardTextareaForm'
import TextEditor from '@/components/TextEditor.vue'
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict'
import format from 'date-fns/format'
import store from '../store'
import { isInputEmpty } from '@/utils/validate'
import { sanitizeRichText } from '@/utils/sanitize'
import { isDarkTheme } from '@/composables/useAppTheme'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog'
import { EventBus } from '@/event-bus/event-bus.js'
import { mapState } from 'vuex'
import { DELETED_USER_NAME } from '@/utils/constants'

export default {
  components: {
    CardUserAvatar,
    TextEditor,
    DeleteConfirmationDialog
    // BpmnCardTextareaForm
  },

  props: ['reply', 'cardId', 'currentUser', 'replyFormVisible', 'withDates', 'hideAvatar', 'showAi', 'miniMode'],

  data () {
    return {
      loading: false,
      editMode: false,
      showDeleteDialog: false,
      updatedText: this.reply.text
    }
  },

  watch: {
    replyFormVisible (newValue) {
      if (newValue) {
        this.editMode = false
      }
    }
  },

  mounted () {
    if (this.reply.isDummy) {
      this.editMode = true
      this.$nextTick(() => {
        this.$el.focus()
        this.$refs.textEditor.setCaretAtEndOfEditor()
      })
    }

    EventBus.$on('edit-card-comments', item => {
      if (item.replyId === this.reply.id) {
        if (!this.miniMode && this.$refs.textEditor) {
          this.toggleEditForm()
          this.$nextTick(() => {
            this.$el.focus()
            this.$refs.textEditor.setCaretAtEndOfEditor()
          })
        }
      }
    })
  },

  beforeUnmount () {
    EventBus.$off('edit-card-comments')
  },

  methods: {
    formatDistanceToNowStrict,
    format,
    isDarkTheme,

    toggleEditForm () {
      const nextValue = !this.editMode
      if (nextValue === false) {
        if (this.reply.text) {
          this.updatedText = this.reply.text
          this.$refs.textEditor.updateValue(this.reply.text)
        }
      }
      this.editMode = nextValue
    },

    openEditForm () {
      this.editMode = true
      this.$emit('hide-reply-form')
    },

    openSideBarEdit () {
      const events = this.$store.state.workflow.workflow.domainEvents
      const targetEvent = events.find((event) => event.cards.find((card) => card.id === this.cardId))

      if (targetEvent) {
        EventBus.$emit('open-card-comments', {
          eventId: targetEvent.id,
          cardId: this.cardId
        })

        setTimeout(() => {
          EventBus.$emit('edit-card-comments', {
            eventId: targetEvent.id,
            cardId: this.cardId,
            replyId: this.reply.id
          })
        }, 10)
      }
    },

    handleEditReply () {
      if (this.miniMode) {
        this.openSideBarEdit()
      } else {
        this.toggleEditForm()
      }
    },

    async onTextUpdate (text) {
      if (isInputEmpty(text)) {
        this.$emit('hide-reply-form')
        this.editMode = false
        if (this.reply.text) {
          this.updatedText = this.reply.text
          this.$refs.textEditor.updateValue(this.reply.text)
        }
      }
    },

    async submitReply (text) {
      if (isInputEmpty(text)) {
        this.$emit('hide-reply-form')
        this.editMode = false
        if (this.reply.text) {
          this.updatedText = this.reply.text
          this.$refs.textEditor.updateValue(this.reply.text)
        }
        return
      }

      if (!this.editMode) return

      const workflowId = this.$route.params.id
      const cardId = this.cardId
      const { id } = this.reply

      if (id) {
        await store.dispatch('workflow/updateCardReply', {
          workflowId,
          cardId,
          id,
          input: {
            text
          }
        })
      } else {
        await store.dispatch('workflow/addCardReply', {
          workflowId,
          cardId,
          input: {
            text,
            generatedByAi: false
          }
        })
      }

      this.$emit('hide-reply-form')
      this.editMode = false
    },

    async submitToAi (text) {
      if (!this.workflow.aiDescription) {
        await store.dispatch('workflow/openWorkflowDescriptionDialog')
        return
      }

      this.submitReply(text)
      this.$emit('hide-reply-form')
      this.editMode = false

      this.loading = true

      const workflowId = this.$route.params.id
      const cardId = this.cardId
      this.$emit('ask-for-reply', { text, cardId, workflowId })

      this.loading = false
    },

    async deleteCardComment () {
      const workflowId = this.$route.params.id
      const cardId = this.cardId
      const { id } = this.reply
      await store.dispatch('workflow/deleteCardReply', {
        workflowId,
        cardId,
        id
      })
      this.showDeleteDialog = false
    }
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      projectMemberships: state => state.projectMemberships.projectMemberships
    }),
    isAuthor () {
      return this.reply.author?.id === this.currentUser?.id
    },
    authorName () {
      const { author } = this.reply

      return author ? `${author.firstname} ${author.lastname}` : DELETED_USER_NAME
    },
    isProjectOwner () {
      const membership = this.projectMemberships?.find(membership => membership.project?.id === this.workflow?.projectId)
      return Boolean(this.currentUser?.id) && this.currentUser?.id === membership?.project?.organisation?.userId
    },
    // Author or project owner may delete; editing stays author-only.
    canDelete () {
      return this.isAuthor || this.isProjectOwner
    },
    isDisabled () {
      return isInputEmpty(this.updatedText)
    },
    sanitizedReplyText () {
      return sanitizeRichText(this.reply?.text)
    }
  }
}
</script>

<style scoped>
.card-reply {
  margin-left: 32px;
  border-radius: 8px;
  padding: 10px 12px;
  background-color: var(--light-1);
  box-shadow: 0 2px 4px 0 #00000026;

  &:hover {
    .actions {
      .hidden-action {
        opacity: 1
      }
    }
  }
}

.textEditor {
  border: none !important;
  min-height: 42px !important;
}

::v-deep(.card-reply-button) {
  border-radius: 5px !important;

  &.v-btn.v-btn--size-small.v-btn--variant-outlined {
    border-radius: 5px !important;
    padding: 5px 12px !important;

    &[disabled]{
      opacity: 0.5;
    }
  }

  &:not(:last-child) {
    margin-right: 5px;
  }
}

[data-theme="dark"] .card-reply-button .logo {
  filter: brightness(0) invert(1);
}

.reply-time {
  color: var(--dark-2);
  font-size: 12px;
  font-weight: 400;
  text-align: left;
  line-height: 15px;
  letter-spacing: -0.42px;
}

.actions {
  gap: 8px;
  color: var(--dark-2);
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  button {
    opacity: 1;
  }

  .hidden-action {
    opacity: 0;
  }
}

.action-wrapper {
  display: inline-flex;
}

/* Make disabled action buttons read as disabled (overrides .actions button { opacity: 1 }). */
.actions :deep(.v-btn.v-btn--disabled) {
  opacity: 0.4;
}

.avatar {
  margin-left: 4px;
  margin-right: -4px;
}

.text {
  padding: 1px;
  font-size: 14px;
  line-height: 19.88px;
}
</style>
